import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel,
  IconButton, Stack, Chip, Grid, Tab, Tabs, Alert, LinearProgress,
  FormControlLabel, Switch, Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountIcon,
  AutoAwesome as SeedIcon,
} from '@mui/icons-material';
import { useAuth } from '../auth/AuthProvider';
import { useParams } from 'react-router-dom';
import { principalProfileApi } from '../api/services';
import type { PrincipalProfileEntry, PrincipalReminder } from '../types/models';

const SECTIONS = ['Personal', 'Family', 'Preferences', 'Travel', 'Stakeholders', 'Protocols', 'Dates', 'Health'];

const SENSITIVITY_OPTIONS = ['Standard', 'Sensitive', 'HighlyConfidential'];

const sensitivityConfig: Record<string, { color: string; bg: string; label: string }> = {
  Standard: { color: '#8892A8', bg: 'rgba(136,146,168,0.12)', label: 'Standard' },
  Sensitive: { color: '#FFB74D', bg: 'rgba(255,183,77,0.12)', label: 'Sensitive' },
  HighlyConfidential: { color: '#FF5252', bg: 'rgba(255,82,82,0.12)', label: 'Highly Confidential' },
};

const sectionColors: Record<string, string> = {
  Personal: '#6C9BFF',
  Family: '#CE93D8',
  Preferences: '#69F0AE',
  Travel: '#FFB74D',
  Stakeholders: '#FF8A65',
  Protocols: '#80DEEA',
  Dates: '#F48FB1',
  Health: '#A5D6A7',
};

export default function PrincipalProfilePage() {
  const { user } = useAuth();
  const { execId: paramExecId } = useParams<{ execId?: string }>();
  const execId = paramExecId || user?.id || '';

  const [profile, setProfile] = useState<Record<string, PrincipalProfileEntry[]>>({});
  const [reminders, setReminders] = useState<PrincipalReminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editEntry, setEditEntry] = useState<Partial<PrincipalProfileEntry> | null>(null);
  const [editingInlineId, setEditingInlineId] = useState<string | null>(null);
  const [inlineValue, setInlineValue] = useState('');
  const [seeding, setSeeding] = useState(false);

  const isEmpty = Object.keys(profile).length === 0 ||
    Object.values(profile).every(entries => entries.length === 0);

  const loadProfile = useCallback(async () => {
    if (!execId) return;
    setLoading(true);
    setError(null);
    try {
      const [profileData, reminderData] = await Promise.all([
        principalProfileApi.getProfile(execId),
        principalProfileApi.getReminders(execId, 30),
      ]);
      setProfile(profileData);
      setReminders(reminderData);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [execId]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const handleSeed = async () => {
    if (!window.confirm('Seed 68 template entries? This will create a starting template for the profile.')) return;
    setSeeding(true);
    try {
      await principalProfileApi.seedTemplate(execId);
      await loadProfile();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to seed template');
    } finally {
      setSeeding(false);
    }
  };

  const handleSave = async () => {
    if (!editEntry?.label || !editEntry?.section || !editEntry?.category) return;
    try {
      if (editEntry.id) {
        await principalProfileApi.updateEntry(execId, editEntry.id, editEntry);
      } else {
        await principalProfileApi.addEntry(execId, editEntry);
      }
      setDialogOpen(false);
      setEditEntry(null);
      loadProfile();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save entry');
    }
  };

  const handleDelete = async (entryId: string) => {
    if (!window.confirm('Delete this profile entry?')) return;
    try {
      await principalProfileApi.deleteEntry(execId, entryId);
      loadProfile();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to delete');
    }
  };

  const handleInlineSave = async (entry: PrincipalProfileEntry) => {
    try {
      await principalProfileApi.updateEntry(execId, entry.id, { ...entry, value: inlineValue });
      setEditingInlineId(null);
      loadProfile();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update');
    }
  };

  const openAddDialog = (section: string, category: string) => {
    setEditEntry({
      section,
      category,
      label: '',
      value: '',
      notes: null,
      sensitivity: 'Standard',
      dateValue: null,
      isAnnualRecurrence: false,
      reminderDaysBefore: 7,
      displayOrder: 0,
    });
    setDialogOpen(true);
  };

  const openEditDialog = (entry: PrincipalProfileEntry) => {
    setEditEntry({ ...entry });
    setDialogOpen(true);
  };

  // Get entries for the currently selected section tab
  const selectedSection = SECTIONS[tabValue];
  const sectionEntries = profile[selectedSection] || [];

  // Group entries by category within the section
  const categoriesInSection = sectionEntries.reduce<Record<string, PrincipalProfileEntry[]>>((acc, entry) => {
    if (!acc[entry.category]) acc[entry.category] = [];
    acc[entry.category].push(entry);
    return acc;
  }, {});

  // Sort entries within each category by displayOrder
  Object.values(categoriesInSection).forEach(entries =>
    entries.sort((a, b) => a.displayOrder - b.displayOrder)
  );

  const totalEntries = Object.values(profile).reduce((sum, entries) => sum + entries.length, 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AccountIcon sx={{ color: 'primary.main', fontSize: 32 }} />
          <Box>
            <Typography variant="h4">Principal Profile</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Intelligence about your executive — {totalEntries} entries
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1}>
          {isEmpty && !loading && (
            <Button
              variant="contained"
              startIcon={<SeedIcon />}
              onClick={handleSeed}
              disabled={seeding}
              color="secondary"
            >
              {seeding ? 'Seeding...' : 'Seed Template'}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openAddDialog(selectedSection, '')}
          >
            Add Entry
          </Button>
        </Stack>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Reminders Panel */}
      {reminders.length > 0 && (
        <Card sx={{ mb: 3, borderLeft: 3, borderColor: '#F48FB1' }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <NotificationsIcon sx={{ color: '#F48FB1', fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ color: '#F48FB1' }}>
                Upcoming Dates — Next 30 Days
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {reminders.map((r) => (
                <Chip
                  key={r.id}
                  label={`${r.label} — ${r.daysUntil === 0 ? 'Today' : r.daysUntil === 1 ? 'Tomorrow' : `${r.daysUntil} days`}`}
                  size="small"
                  sx={{
                    bgcolor: r.daysUntil <= 3 ? 'rgba(255,82,82,0.15)' : r.daysUntil <= 7 ? 'rgba(255,183,77,0.15)' : 'rgba(108,155,255,0.1)',
                    color: r.daysUntil <= 3 ? '#FF5252' : r.daysUntil <= 7 ? '#FFB74D' : '#6C9BFF',
                    fontWeight: 600,
                  }}
                />
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Section Tabs */}
      <Tabs
        value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3 }}
      >
        {SECTIONS.map((section) => {
          const count = (profile[section] || []).length;
          return (
            <Tab
              key={section}
              label={`${section} (${count})`}
              sx={{
                '&.Mui-selected': { color: sectionColors[section] },
              }}
            />
          );
        })}
      </Tabs>

      {/* Empty state for section */}
      {!loading && Object.keys(categoriesInSection).length === 0 && (
        <Card sx={{ p: 6, textAlign: 'center' }}>
          <Typography color="text.secondary" gutterBottom>
            No entries in {selectedSection}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => openAddDialog(selectedSection, '')}
            sx={{ mt: 1 }}
          >
            Add First Entry
          </Button>
        </Card>
      )}

      {/* Category Groups */}
      {Object.entries(categoriesInSection).map(([category, entries]) => (
        <Box key={category} sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ color: sectionColors[selectedSection] || 'primary.main', fontWeight: 600 }}>
              {category}
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => openAddDialog(selectedSection, category)}
            >
              Add
            </Button>
          </Box>
          <Grid container spacing={2}>
            {entries.map((entry) => {
              const sens = sensitivityConfig[entry.sensitivity] || sensitivityConfig.Standard;
              return (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={entry.id}>
                  <Card sx={{
                    borderLeft: 3,
                    borderColor: sectionColors[selectedSection] || 'primary.main',
                    height: '100%',
                  }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              {entry.label}
                            </Typography>
                            {entry.sensitivity === 'HighlyConfidential' && (
                              <Tooltip title="Highly Confidential">
                                <LockIcon sx={{ fontSize: 14, color: '#FF5252' }} />
                              </Tooltip>
                            )}
                          </Stack>
                          {/* Inline editable value */}
                          {editingInlineId === entry.id ? (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <TextField
                                size="small"
                                value={inlineValue}
                                onChange={(e) => setInlineValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleInlineSave(entry);
                                  if (e.key === 'Escape') setEditingInlineId(null);
                                }}
                                autoFocus
                                sx={{ flex: 1 }}
                              />
                              <Button size="small" onClick={() => handleInlineSave(entry)}>Save</Button>
                              <Button size="small" onClick={() => setEditingInlineId(null)}>Cancel</Button>
                            </Stack>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.primary',
                                cursor: 'pointer',
                                '&:hover': { color: 'primary.main' },
                              }}
                              onClick={() => {
                                setEditingInlineId(entry.id);
                                setInlineValue(entry.value);
                              }}
                            >
                              {entry.value || '(click to set)'}
                            </Typography>
                          )}
                          {/* Date countdown */}
                          {entry.dateValue && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              {formatDateCountdown(entry.dateValue)}
                            </Typography>
                          )}
                          {/* Notes */}
                          {entry.notes && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
                              {entry.notes}
                            </Typography>
                          )}
                        </Box>
                        <Stack direction="row" sx={{ ml: 1, flexShrink: 0 }}>
                          <Chip
                            label={sens.label}
                            size="small"
                            sx={{ bgcolor: sens.bg, color: sens.color, height: 20, fontSize: '0.65rem', mr: 0.5 }}
                          />
                          <IconButton size="small" onClick={() => openEditDialog(entry)}>
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDelete(entry.id)}>
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Stack>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ))}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editEntry?.id ? 'Edit Entry' : 'New Entry'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Section</InputLabel>
              <Select
                value={editEntry?.section || selectedSection}
                label="Section"
                onChange={(e) => setEditEntry(prev => ({ ...prev, section: e.target.value }))}
              >
                {SECTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Category"
              fullWidth
              value={editEntry?.category || ''}
              onChange={(e) => setEditEntry(prev => ({ ...prev, category: e.target.value }))}
              placeholder="e.g. Contact Details, Dietary Requirements"
            />
            <TextField
              label="Label"
              fullWidth
              value={editEntry?.label || ''}
              onChange={(e) => setEditEntry(prev => ({ ...prev, label: e.target.value }))}
              placeholder="e.g. Mobile Number, Spouse Name"
            />
            <TextField
              label="Value"
              fullWidth
              value={editEntry?.value || ''}
              onChange={(e) => setEditEntry(prev => ({ ...prev, value: e.target.value }))}
              multiline
              rows={2}
            />
            <TextField
              label="Notes (optional)"
              fullWidth
              value={editEntry?.notes || ''}
              onChange={(e) => setEditEntry(prev => ({ ...prev, notes: e.target.value || null }))}
              multiline
              rows={2}
            />
            <FormControl fullWidth>
              <InputLabel>Sensitivity</InputLabel>
              <Select
                value={editEntry?.sensitivity || 'Standard'}
                label="Sensitivity"
                onChange={(e) => setEditEntry(prev => ({ ...prev, sensitivity: e.target.value }))}
              >
                {SENSITIVITY_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Date Value (optional)"
              type="date"
              fullWidth
              value={editEntry?.dateValue || ''}
              onChange={(e) => setEditEntry(prev => ({ ...prev, dateValue: e.target.value || null }))}
              InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editEntry?.isAnnualRecurrence || false}
                  onChange={(e) => setEditEntry(prev => ({ ...prev, isAnnualRecurrence: e.target.checked }))}
                />
              }
              label="Annual recurrence (e.g. birthday)"
            />
            <TextField
              label="Reminder Days Before"
              type="number"
              fullWidth
              value={editEntry?.reminderDaysBefore ?? 7}
              onChange={(e) => setEditEntry(prev => ({ ...prev, reminderDaysBefore: parseInt(e.target.value) || 0 }))}
            />
            <TextField
              label="Display Order"
              type="number"
              fullWidth
              value={editEntry?.displayOrder ?? 0}
              onChange={(e) => setEditEntry(prev => ({ ...prev, displayOrder: parseInt(e.target.value) || 0 }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function formatDateCountdown(dateStr: string): string {
  try {
    const target = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);
    const diffMs = target.getTime() - today.getTime();
    const days = Math.round(diffMs / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    if (days === -1) return 'Yesterday';
    if (days > 0) return `${days} days away`;
    return `${Math.abs(days)} days ago`;
  } catch {
    return dateStr;
  }
}
